import { Cpu } from 'lucide-react'

import type { PublicPatient } from '@snootsvet/shared/portal'

import {
  EditActions,
  EditableField,
  EditableValue,
  EditButton,
  useEditableField,
} from '@/customer/components/editable-field'
import { Input } from '@/customer/components/ui/input'
import { useUpdatePet } from '@/customer/hooks/use-update-pet'

/**
 * Microchip number row. Often unset at registration and filled in
 * later once the chip is implanted, so always editable. Empty input
 * clears the value.
 */
export function MicrochipPetField({ pet }: { pet: PublicPatient }) {
  const updatePet = useUpdatePet(pet.id)
  const microchipField = useEditableField({
    initialValue: pet.microchipNumber ?? '',
    onSave: async ({ value, setError }) => {
      const next = value.trim() || null
      if (next === pet.microchipNumber) return
      try {
        await updatePet.mutateAsync({ microchipNumber: next })
      } catch {
        setError('Something went wrong saving. Please try again.')
      }
    },
  })

  return (
    <EditableField
      label="Microchip"
      icon={Cpu}
      error={microchipField.error}
      action={<EditButton field={microchipField} label="microchip" />}
    >
      {microchipField.isEditing ? (
        <Input
          autoFocus
          variant="underline"
          value={microchipField.draft}
          onChange={(e) => microchipField.setDraft(e.target.value)}
          placeholder="e.g. 900123456789012"
          autoComplete="off"
        />
      ) : (
        <EditableValue>
          {pet.microchipNumber ?? <span className="text-muted-foreground">Not set</span>}
        </EditableValue>
      )}
      <EditActions field={microchipField} />
    </EditableField>
  )
}
